import React, { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { maestrosBdCorpoServices, maestrosPNPServices } from '@/services/maestros.service';
import { maestrosTramiteDefault, useMaestrosStore } from '@/store/maestros.store';
import { useSessionStore } from '@/store/session.store';

import type { IGlobalContext } from '@/interfaces/context/IGlobal.context.interface';
import type { IMaestrosGeneric } from '@/interfaces/model/maestros.interface';
import { makeObjEmptyArray } from '@/utils/object/object';

const maestrosGenericServices = {
	...maestrosPNPServices,
	...maestrosBdCorpoServices,
};

const defaultMaestros = {
	maestrosGeneric: makeObjEmptyArray<IMaestrosGeneric>(Object.keys(maestrosGenericServices)),
	maestrosTramite: maestrosTramiteDefault,
};

const contextDefaultValue: IGlobalContext = {
	// USER STORE
	user: undefined,
	setUser: () => undefined,
	cleanMaestroTramite: () => undefined,
	tramiteSeleccionado: 0,
	getTramitesContratados: () => [],
	getTramiteData: () => ({
		tipoTramite: { id: 0, descripcion: 'dummy' },
		contratado: false,
		codigo: 'dummy',
		icono: 'user',
		descripcionAmpliada: 'dummy',
		tag: 'dummy',
	}),

	// MAESTROS STORE
	setTramiteSeleccionado: () => undefined,
	maestrosGeneric: defaultMaestros.maestrosGeneric,
	maestrosTramite: defaultMaestros.maestrosTramite,
	loadmaestrosTramite: () => undefined,

	clean: () => undefined,
	// @ts-expect-error pass
	globalHeaders: undefined,
};

type GlobalContextProviderProps = {
	readonly children: ReactNode;
};

const GlobalContext = createContext<IGlobalContext>(contextDefaultValue);

const GlobalContextProvider: React.FC<GlobalContextProviderProps> = ({ children }) => {
	// USER STORE
	const user = useSessionStore((s) => s.user);
	const setUser = useSessionStore((s) => s.setUser);
	const getTramiteData = useSessionStore((s) => s.getTramiteData);
	const getTramitesContratados = useSessionStore((s) => s.getTramitesContratados);
	const cleanSession = useSessionStore((s) => s.clean);
	const setTramiteSeleccionado = useSessionStore((s) => s.setTramiteSeleccionado);
	const tramiteSeleccionado = useSessionStore((s) => s.tramiteSeleccionado);

	// MAESTROS STORE
	const maestrosGeneric = useMaestrosStore((s) => s.maestrosGeneric);
	const maestrosTramite = useMaestrosStore((s) => s.maestrosTramite);
	const fetchMaestrosGeneric = useMaestrosStore((s) => s.fetchMaestrosGeneric);
	const fetchMaestrosTramite = useMaestrosStore((s) => s.fetchmaestrosTramite);
	const cleanMaestros = useMaestrosStore((s) => s.clean);
	const cleanMaestroTramite = useMaestrosStore((s) => s.cleanMaestroTramite);

	// --- MENSAJES (ESTADOS DUMMY PARA QUE NO FALLE) ---
	const isOpenHelpModal = false;

	// Aquí puedes meter datos mock si quieres ver algo en los filtros del sidebar
	const maestrosPNM = {
		tiposNotificaciones: [
			{
				id: 1,
				tipoTramite: { id: 1, descripcion: 'PPO' },
				tipoNotificacion: { id: 10, descripcion: 'Aviso de Pago' },
			},
			{
				id: 2,
				tipoTramite: { id: 2, descripcion: 'CREG' },
				tipoNotificacion: { id: 11, descripcion: 'Nueva Solicitud' },
			},
		],
	};

	const globalHeaders = {
		Authorization: `Bearer ${user?.token}`,
		'X-Codigo-EF': '0000',
	};
	// --- FIN MENSAJES ---

	const clean = () => {
		cleanSession();
		cleanMaestros();
	};

	useEffect(() => {
		if (user?.token) {
			fetchMaestrosGeneric();
		}
	}, [user, fetchMaestrosGeneric]);

	const value: IGlobalContext = useMemo(
		() => ({
			// USER STORE
			user,
			setUser,
			getTramitesContratados,
			getTramiteData,
			tramiteSeleccionado,
			setTramiteSeleccionado,

			// MAESTROS STORE
			maestrosGeneric,
			maestrosTramite,
			loadmaestrosTramite: fetchMaestrosTramite,
			cleanMaestroTramite,

			// MENSAJES
			globalHeaders,
			maestrosPNM,
			clean,
		}),
		[
			user,
			maestrosGeneric,
			maestrosTramite,
			tramiteSeleccionado,
			fetchMaestrosTramite,
			isOpenHelpModal,
			maestrosPNM,
		],
	);

	return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

const useGlobalContext = (): IGlobalContext => {
	const context = useContext(GlobalContext);
	if (context === null) {
		throw new Error('useGlobalContext must be used within provider');
	}
	return context;
};

export { GlobalContext, GlobalContextProvider, useGlobalContext };
